import apiV2 from '../api/v2';
import type { TiptapDoc } from '@odo/shared-ui';
import type {
    VirtualAppointmentCancellation,
    VirtualAppointmentStatus,
    VirtualAppointmentTeam,
    VirtualAppointmentTriggerKind
} from './virtualAppointments';

export type VirtualAppointmentOverviewSort = 'createdAt' | 'dueDate' | 'patientName';
// The API only takes a direction for createdAt; the other sorts have a fixed order.
export type VirtualAppointmentOverviewOrder = 'asc' | 'desc';

export const VIRTUAL_APPOINTMENT_OVERVIEW_PAGE_SIZE = 25;

export interface VirtualAppointmentOverviewPatient {
    id: string;
    name: string;
    practiceId: string | null;
    practiceName: string | null;
    internalCaseStatus: string | null;
    caseStatus: string | null;
    currentAligner: number | null;
    totalAligners: number | null;
    /** Mirrored DM state (Started, Resumed, Paused, Stopped, Unstarted); null when never linked. */
    monitoringStatus: string | null;
    /** Has an open or due VA while outside the Treatment steps, where no VA can be worked. */
    pendingVaOutsideTreatment: boolean;
    /** UTC day the current Treatment phase began, which week triggers count from; null outside it. */
    treatmentStartDate: string | null;
}

export interface VirtualAppointmentOverviewLate {
    reason: 'blocked' | 'orphaned';
}

// One row of the admin-only cross-patient list (GET /virtual-appointments).
export interface VirtualAppointmentOverviewItem {
    id: string;
    typeCode: string | null;
    typeLabel: string | null;
    typeCustom: string | null;
    team: VirtualAppointmentTeam;
    assigneeEmail: string | null;
    status: VirtualAppointmentStatus;
    triggerKind: VirtualAppointmentTriggerKind;
    triggerWeeks: number | null;
    /** The instant (ISO) a "from now" week trigger counts from; null counts from the treatment start. */
    triggerWeeksFrom: string | null;
    triggerAligner: number | null;
    triggerNogos: number | null;
    /** No-gos counted so far toward `triggerNogos`; 0 for other kinds. */
    nogoCount: number;
    /** YYYY-MM-DD a weeks VA falls due, from its anchor or the Treatment-phase start; null when neither. */
    dueDate: string | null;
    description: string | null;
    createdAt: string;
    updatedAt: string;
    resolvedAt: string | null;
    /** Planned and reached by its aligner, treatment week or no-go count, or opened now. */
    isDue: boolean;
    /** Stuck on the patient's status: due but out of the trigger's reach, or open outside the VA steps. */
    late: VirtualAppointmentOverviewLate | null;
    patient: VirtualAppointmentOverviewPatient;
}

export interface VirtualAppointmentOverviewPerson {
    name: string | null;
    email: string;
}

export interface VirtualAppointmentOverviewDetail extends VirtualAppointmentOverviewItem {
    descriptionDoc: TiptapDoc | null;
    createdBy: VirtualAppointmentOverviewPerson | null;
    /** Who marked it done or canceled it; null while it is live or when unrecorded. */
    resolvedBy: VirtualAppointmentOverviewPerson | null;
    cancellation: VirtualAppointmentCancellation | null;
}

export interface VirtualAppointmentOverviewPage {
    items: VirtualAppointmentOverviewItem[];
    nextCursor: string | null;
    /** Rows matching the filters, regardless of cursor or page. */
    total: number;
}

// Cursor paging backs infinite scroll; page numbers back the pager. The API rejects both at once.
export type VirtualAppointmentOverviewPosition = { cursor: string | null } | { page: number };

export interface VirtualAppointmentOverviewQuery {
    statuses: readonly VirtualAppointmentStatus[];
    teams: readonly VirtualAppointmentTeam[];
    typeCodes: readonly string[];
    /** true adds custom-typed VAs to the type filter; undefined leaves them unfiltered. */
    custom?: boolean;
    practiceIds: readonly string[];
    q: string;
    sort: VirtualAppointmentOverviewSort;
    /** Sent with the createdAt sort only. */
    order?: VirtualAppointmentOverviewOrder;
}

interface ApiEnvelope<T> {
    success: boolean;
    data: T;
}

function csv(values: readonly string[]): string | undefined {
    return values.length > 0 ? values.join(',') : undefined;
}

// The API reads list params as comma-separated values; absent keys apply no filter.
export function toOverviewParams(
    query: VirtualAppointmentOverviewQuery,
    position: VirtualAppointmentOverviewPosition
): Record<string, string | number> {
    const params: Record<string, string | number | undefined> = {
        status: csv(query.statuses),
        team: csv(query.teams),
        typeCode: csv(query.typeCodes),
        custom: query.custom === undefined ? undefined : String(query.custom),
        practiceId: csv(query.practiceIds),
        q: query.q.trim() || undefined,
        sort: query.sort,
        order: query.sort === 'createdAt' ? query.order : undefined,
        limit: VIRTUAL_APPOINTMENT_OVERVIEW_PAGE_SIZE,
        cursor: 'cursor' in position ? (position.cursor ?? undefined) : undefined,
        page: 'page' in position ? position.page : undefined
    };
    return Object.fromEntries(
        Object.entries(params).filter(
            (entry): entry is [string, string | number] => entry[1] !== undefined
        )
    );
}

export async function fetchVirtualAppointmentsOverviewPage(
    query: VirtualAppointmentOverviewQuery,
    position: VirtualAppointmentOverviewPosition
): Promise<VirtualAppointmentOverviewPage> {
    const response = await apiV2.get<ApiEnvelope<VirtualAppointmentOverviewPage>>(
        '/virtual-appointments',
        { params: toOverviewParams(query, position) }
    );
    return response.data.data;
}

export async function fetchVirtualAppointmentOverviewDetail(
    vaId: string
): Promise<VirtualAppointmentOverviewDetail> {
    const response = await apiV2.get<ApiEnvelope<VirtualAppointmentOverviewDetail>>(
        `/virtual-appointments/${encodeURIComponent(vaId)}`
    );
    return response.data.data;
}
